import React from 'react';
import { Info, WifiZero, WifiLow, WifiHigh, Wifi } from 'lucide-react';

function StrategyIcon({ icon: Icon, bgColor, iconColor }: { 
  icon: React.ElementType; 
  bgColor: string;
  iconColor: string;
}) {
  return (
    <div className={`${bgColor} rounded-full p-3`}>
      <Icon className={`h-6 w-6 ${iconColor}`} />
    </div>
  );
}

export function StrategyDetails() {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="max-w-4xl mx-auto">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Info className="h-5 w-5 text-gray-600" />
          Investment Strategy Details
        </h3>

        <div className="prose max-w-none">
          <p className="text-gray-600 mb-6 leading-relaxed">
            Each investment strategy will be customized for the needs of the community. 
            Your portfolio will provide for the liquidity (cash) needs forecast by your 
            Reserve Study and incorporate your capital expenses and risk profile. 
            Most communities choose a low risk or moderate risk strategy.
          </p>

          <div className="space-y-6">
            <div className="bg-gray-50/95 rounded-lg p-4 transition-all duration-300 hover:bg-gray-50">
              <div className="flex items-start gap-4">
                <StrategyIcon 
                  icon={WifiZero} 
                  bgColor="bg-gray-100"
                  iconColor="text-gray-600"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">Conservative Strategy</h4>
                  <p className="text-gray-700">
                    A conservative cash-based strategy that earns a variable interest rate between 2.5 - 4% annually
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50/95 rounded-lg p-4 transition-all duration-300 hover:bg-blue-50">
              <div className="flex items-start gap-4">
                <StrategyIcon 
                  icon={WifiLow} 
                  bgColor="bg-blue-100"
                  iconColor="text-blue-600"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">Low Risk Strategy</h4>
                  <p className="text-blue-700">
                    A low risk strategy that earns an average annual return forecasted from 
                    laddered government securities and treasuries.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50/95 rounded-lg p-4 transition-all duration-300 hover:bg-green-50">
              <div className="flex items-start gap-4">
                <StrategyIcon 
                  icon={WifiHigh} 
                  bgColor="bg-green-100"
                  iconColor="text-green-600"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-green-900 mb-2">Moderate Risk Strategy</h4>
                  <p className="text-green-700">
                    A moderate risk strategy that allocates 60% to government securities and 
                    treasuries and 40% to highly rated corporate bonds.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50/95 rounded-lg p-4 transition-all duration-300 hover:bg-orange-50">
              <div className="flex items-start gap-4">
                <StrategyIcon 
                  icon={Wifi} 
                  bgColor="bg-orange-100"
                  iconColor="text-orange-600"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-900 mb-2">Higher Risk Strategy</h4>
                  <p className="text-orange-700">
                    A higher risk strategy is not recommended for most communities. This strategy 
                    would allocate 60% to government securities and treasuries, 30% to highly rated 
                    corporate bonds and 10% to SPY.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-100">
            <p className="text-sm text-gray-600 font-medium">
              Note: All scenarios are adjusted for expected inflation over the investment horizon.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}